/**
 * useBoardSelection — derives the effective boardId from URL params + boards list.
 *
 * Priority: URL param > context (persisted) > first board in list.
 * No effect cascades — boardId is derived via useMemo, not synced via useEffect.
 */

import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useBoards } from './useWorkData';
import type { Board } from '../api/work';

interface UseBoardSelectionReturn {
  boards: Board[];
  boardId: string;
  isLoading: boolean;
}

export function useBoardSelection(projectId: string | undefined): UseBoardSelectionReturn {
  const params = useParams<{ boardId?: string }>();
  const { boardId: contextBoardId, setBoardId: setContextBoardId } = useProject();
  const { data: boards = [], isLoading } = useBoards(projectId);

  // Derive effective board ID — URL wins, then context, then first board.
  // IMPORTANT: if the URL has a boardId, return it immediately (even before boards load)
  // so downstream queries (useBoard, useIssues) can start in parallel with useBoards.
  const boardId = useMemo(() => {
    if (params.boardId) return params.boardId;
    if (contextBoardId && (!boards.length || boards.some(b => b.id === contextBoardId))) {
      return contextBoardId;
    }
    return boards[0]?.id ?? '';
  }, [params.boardId, contextBoardId, boards]);

  // One-way sync to context when derived value differs (so other pages see the selection)
  useEffect(() => {
    if (boardId && boardId !== contextBoardId) {
      setContextBoardId(boardId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  return { boards, boardId, isLoading };
}
